import { useContext, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { style, stylesheet, classes } from 'typestyle'

/* Hooks ======================================================================================== */
import useValue from '../../../hooks/use-value'
import useFetch from '../../../hooks/use-fetch'
import useBoolean from '../../../hooks/use-boolean'

/* services ===================================================================================== */
import { getCurrentUser } from '../../../services/auth'

/* Common ======================================================================================= */
import { get } from '../../../share/find'
import { NavigationContext } from '../../../contexts/navigation'
import { addSnippet } from '../../../core/snippet-manager'
import { withRouter } from '../../../contexts/with-router'

/* Constants ==================================================================================== */
import { NAVBAR_WIDTH } from '../../../components/app-layout/app-layout.constants'

/* Components =================================================================================== */
import { Box } from '../../../components/box/box.component'
import { Textarea } from '../../../components/textarea'
import { Button } from '../../../components/button'
import { Text } from '../../../components/text/text.component'
import { Checkbox } from '../../../components/checkbox/checkbox.component'

/* Styles ======================================================================================= */
import { b0 } from '../../../styles/styleset/bottom/b0'
import { mb12 } from '../../../styles/styleset/margin/mb12'
import { mb8 } from '../../../styles/styleset/margin/mb8'
import { mr12 } from '../../../styles/styleset/margin/mr12'
import { mr8 } from '../../../styles/styleset/margin/mr8'
import { mt12 } from '../../../styles/styleset/margin/mt12'
import { p12 } from '../../../styles/styleset/padding/p12'
import { primaryDark } from '../../../styles/styleset/primary-dark/primaryDark'
import { r0 } from '../../../styles/styleset/right/r0'

const View = (props: any) => {
  const { head, match } = props
  const { rowId, groupId } = useParams()

  const history = useNavigate()
  const location = useLocation()
  // const appLayoutContext: any = useContext(props.context)
  const { navigation, retrigger } = useContext(NavigationContext)
  const { currentUser } = getCurrentUser()

  const apiUrl = `${process.env.REACT_APP_API_URL}${location.pathname}`
  const childrenUrl = `${process.env.REACT_APP_API_URL}/a/${
    groupId ? `groups/${groupId}/base` : 'user/base'
  }/messages/parent/${rowId}/table`
  const base = get('payload.base', navigation, 'object')
  const PAGE_LIMIT = 20

  const [value, $value] = useValue('')
  const [allowEnterToSubmit, $allowEnterToSubmit] = useBoolean(false)
  const [messageStack, $messageStack] = useValue([])
  const [row, $row] = useFetch(
    {},
    { url: childrenUrl, search: { lookups: [{ key: 'createdBy' }, { key: 'group' }] } },
  )
  const [, $message] = useFetch({}, { url: `${childrenUrl}/new` })
  const [messages, $messages] = useFetch(
    {
      totalPage: 0,
      data: [],
    },
    {
      url: childrenUrl,
      search: {
        page: 0,
        limit: PAGE_LIMIT,
        sort: { key: 'createdAt', direction: -1 },
        lookups: [{ key: 'createdBy' }],
      },
    },
  )

  useEffect(() => {
    if (row.mounted && Object.keys(base).length > 0) {
      $row.get({ url: apiUrl, search: { lookups: [{ key: 'createdBy' }] } })
    }
  }, [row.mounted, base])

  useEffect(() => {
    if (messages.mounted && Object.keys(navigation.payload).length > 0) {
      $messages.get({ url: childrenUrl }).then((payload: any) => {
        $messageStack.set(payload.data)

        scrollToBottom()
      })
    }
  }, [messages.mounted, navigation.payload])

  useEffect(() => {
    if (retrigger) {
      $messages
        .get({
          url: childrenUrl,
          search: { page: 0, limit: 1, sort: { key: 'createdAt', direction: -1 } },
        })
        .then((payload: any) => {
          $messageStack.set((prev: any) => [...prev, ...payload.data])
          scrollToBottom()
        })
    }
  }, [retrigger])

  const scrollToBottom = () => {
    const scrollingElement = document.scrollingElement || document.body
    scrollingElement.scrollTop = scrollingElement.scrollHeight
  }

  const isAtTop =
    messages.payload.totalPage === (messages.search.page || 1) || messages.payload.data.length === 0

  return (
    <>
      {(isAtTop && Object.keys(row.payload).length > 0
        ? [row.payload, ...messageStack]
        : messageStack
      )
        .sort((a: any, b: any) => {
          if (a.createdAt > b.createdAt) {
            return 1
          } else if (a.createdAt < b.createdAt) {
            return -1
          }
          return 0
        })
        .map((message: any, key: any) => {
          let from = ''

          if (message?.createdBy?.id === currentUser.id) {
            from = 'You'
          } else {
            from = message?.createdBy?.body?.fullName
          }

          if (!from) {
            from = message?.group?.body?.name
          }

          return (
            <Box
              baseline="surface"
              className={classes(
                p12,
                mt12,
                mb12,
                style({
                  alignSelf:
                    message?.createdBy && message?.createdBy.id === currentUser.id
                      ? 'flex-end'
                      : 'flex-start',
                }),
              )}
              flex
              shadow
              rounded
              halfWidth
              key={key}
            >
              <Box baseline="none" fullWidth row>
                <Box className={mb8}>
                  {message.createdBy && message.createdBy.body && (
                    <Text
                      size="xl"
                      bold
                      className={classes(
                        mr8,
                        message.createdBy.id === currentUser.id && primaryDark,
                      )}
                    >
                      {from}
                    </Text>
                  )}
                  <Text size="md" baseline="surface" alt lighter>
                    {new Date(message.createdAt).toLocaleString('en-CA')}
                  </Text>
                </Box>
                <Text className={STYLES.textMessage} size="xl" preWrap>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: message.body.body || message.body.message,
                    }}
                  />
                </Text>
              </Box>
            </Box>
          )
        })}
      {false && (
        <Box className={classes(b0, r0, p12)} baseline="surface" fixed shadow>
          <Box baseline="none" maxWidth={960} fullWidth centerX>
            <Box baseline="surface" row>
              <Box className={mr8} fullWidth>
                <div
                  onKeyDown={(event: any) => {
                    if (event.keyCode === 13 && allowEnterToSubmit) {
                      event.preventDefault()
                      $message.post({ body: { body: { message: value } } })
                      $value.reset()
                    }
                  }}
                >
                  <Textarea
                    size="lg"
                    onChange={value => {
                      const newline = String.fromCharCode(13, 10)

                      $value.set(value.replaceAll('\\n', newline))
                    }}
                    value={value}
                  />
                </div>
              </Box>
              <Box alignRight alignCenterY>
                <Box flex className={mr12}>
                  <Checkbox
                    label="Enter to Submit"
                    value={allowEnterToSubmit}
                    onChange={(value: any) => {
                      $allowEnterToSubmit.set(value)
                    }}
                  />
                </Box>
                <Button
                  size="sm"
                  baseline="primary"
                  label="Send"
                  square
                  onClick={() => {
                    $message.post({ body: { body: { message: value } } })
                    $value.reset()
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  )
}

const STYLES = stylesheet({
  container: {
    width: 'calc(100% - 0px)',
  },
  containerWithNav: {
    width: `calc(100% - ${NAVBAR_WIDTH}px)`,
  },
  textMessage: {
    $nest: {
      a: {
        color: '#6599da',
      },
    },
  },
})

export default addSnippet('Messages', withRouter(View))
